import type { EdgeBar } from '../types/chart'
import { PERIODS, type Period } from './resolution'

/**
 * Sub-bar playback support (spec Addendum §"Sub-bar playback design").
 *
 * A replay steps through SUB-resolution bars aggregated into the display
 * bucket, so a higher-timeframe candle FORMS during replay instead of popping
 * in whole — exactly like watching live. These two helpers are the pure,
 * unit-testable core of that behavior; the ReplayController wires them to the
 * fetch/append machinery.
 *
 * Placement rationale: kept OUT of ReplayController because both are pure
 * functions of their inputs (no controller state) and the spec calls them out
 * as independently unit-testable — same pattern as `resolution.ts`. This keeps
 * the controller focused on scheduling/buffering and lets the ladder + merge
 * rules be pinned in isolation.
 */

/**
 * The sub-resolution ladder: map a display timeframe to the resolution its
 * bars form FROM during replay. Bounds fetch volume and keeps every display
 * bar within ~5–20 steps (spec Addendum §"Sub-resolution ladder"):
 *
 * | Display        | Sub-step |
 * |----------------|----------|
 * | 1m             | 1m (identity — already finest) |
 * | 2m–15m         | 1m       |
 * | 30m, 1H, 4H    | 5m       |
 * | 1D             | 30m      |
 * | 1W / 1M / 1Y   | 1D       |
 *
 * Whole-bar mode is reachable by mapping a TF to itself (1m → 1m); an unknown
 * timespan falls back to identity so replay degrades to whole-bar rather than
 * throwing.
 */
export function subPeriodFor(display: Period): Period {
  switch (display.timespan) {
    case 'minute':
      // ≤15m minute TFs step by 1m; coarser minute TFs (30m) step by 5m.
      return display.multiplier <= 15 ? PERIODS['1m'] : PERIODS['5m']
    case 'hour':
      return PERIODS['5m']
    case 'day':
      return PERIODS['30m']
    case 'week':
    case 'month':
    case 'year':
      return PERIODS['1D']
    default:
      // Unknown/missing timespan → identity (whole-bar fallback).
      return display
  }
}

/**
 * The next coarser rung when a sub-resolution has NO data for the replayed
 * range (finer resolutions have shorter retention — 1m may not reach a date
 * the 5m/daily series covers). Walks 1m → 5m → 30m → 1D, capped at the
 * display period itself (= whole-bar mode); null when already at/above the
 * display resolution — nothing coarser to try.
 */
export function nextSubRung(current: Period, display: Period): Period | null {
  if (current.ms >= display.ms) return null
  const rungs = [PERIODS['5m'], PERIODS['30m'], PERIODS['1D']]
  for (const rung of rungs) {
    if (rung.ms > current.ms && rung.ms < display.ms) return rung
  }
  return display
}

/**
 * Merge one sub-bar into the forming display bar, stamped at `bucketTs` (the
 * display-bucket boundary the sub-bar falls in). This mirrors how a live tick
 * accretes into the current candle:
 *
 * - `forming === null` (bucket just opened) → a fresh bar OPENING at the sub's
 *   open, stamped at `bucketTs`.
 * - otherwise → extend high/low, take the sub's close as the running close,
 *   and accumulate volume. The open is fixed at the bucket's first sub-bar.
 *
 * Volumes are treated as 0 when undefined so a feed that omits volume can't
 * poison the accumulator with `NaN`.
 */
export function mergeSubBar(forming: EdgeBar | null, sub: EdgeBar, bucketTs: number): EdgeBar {
  const subVol = sub.volume ?? 0
  if (forming == null) {
    return {
      timestamp: bucketTs,
      open: sub.open,
      high: sub.high,
      low: sub.low,
      close: sub.close,
      volume: subVol,
    }
  }
  return {
    timestamp: bucketTs,
    open: forming.open,
    high: Math.max(forming.high, sub.high),
    low: Math.min(forming.low, sub.low),
    close: sub.close,
    volume: (forming.volume ?? 0) + subVol,
  }
}
